import React, { useEffect, useState, useRef } from 'react';
import './About.css';

const stats = [
  { target: 3, suffix: '+', description: 'Years of research-led innovation in livestock health technology.' },
  { target: 13000, prefix: '₹', suffix: ' Cr', description: 'Estimated dairy losses in India — we\u2019re on a mission to reduce it.' },
  { target: 200, suffix: 'M+', description: 'Litres of milk at risk annually — we aim to protect every drop.' },
  { target: 1, suffix: ' Goal', description: 'Healthier animals. Stronger farms. Sustainable dairy futures.' }
];

const About = () => {
  const [counts, setCounts] = useState(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const applyMobileStyles = () => {
      const width = window.innerWidth;
      const isMobile = width <= 768;

      if (isMobile) {
        const aboutSection = document.querySelector('.about-section');
        const aboutContainer = document.querySelector('.about-container');
        const aboutTitle = document.querySelector('.about-title');

        if (aboutSection) {
          aboutSection.style.setProperty('padding-left', '0', 'important');
          aboutSection.style.setProperty('padding-right', '0', 'important');
        }
        if (aboutContainer) {
          aboutContainer.style.setProperty('padding-left', width <= 390 ? '8px' : '15px', 'important');
          aboutContainer.style.setProperty('padding-right', width <= 390 ? '8px' : '15px', 'important');
          aboutContainer.style.setProperty('margin-left', 'auto', 'important');
          aboutContainer.style.setProperty('margin-right', 'auto', 'important');
          aboutContainer.style.setProperty('width', '100%', 'important');
          aboutContainer.style.setProperty('box-sizing', 'border-box', 'important');
        }
        if (aboutTitle) {
          aboutTitle.style.setProperty('margin-left', 'calc(-50vw + 50%)', 'important');
          aboutTitle.style.setProperty('padding-left', width <= 390 ? '8px' : '15px', 'important');
          aboutTitle.style.setProperty('padding-right', width <= 390 ? '8px' : '15px', 'important');
          aboutTitle.style.setProperty('text-align', 'center', 'important');
          aboutTitle.style.setProperty('width', '100vw', 'important');
          aboutTitle.style.setProperty('box-sizing', 'border-box', 'important');
        }
      } else {
        const aboutSection = document.querySelector('.about-section');
        const aboutContainer = document.querySelector('.about-container');
        const aboutTitle = document.querySelector('.about-title');

        if (aboutSection) {
          aboutSection.style.removeProperty('padding-left');
          aboutSection.style.removeProperty('padding-right');
        }
        if (aboutContainer) {
          aboutContainer.style.removeProperty('padding-left');
          aboutContainer.style.removeProperty('padding-right');
          aboutContainer.style.removeProperty('margin-left');
          aboutContainer.style.removeProperty('margin-right');
          aboutContainer.style.removeProperty('width');
          aboutContainer.style.removeProperty('box-sizing');
        }
        if (aboutTitle) {
          aboutTitle.style.removeProperty('margin-left');
          aboutTitle.style.removeProperty('padding-left');
          aboutTitle.style.removeProperty('padding-right');
          aboutTitle.style.removeProperty('text-align');
          aboutTitle.style.removeProperty('width');
          aboutTitle.style.removeProperty('box-sizing');
        }
      }
    };

    applyMobileStyles();
    window.addEventListener('resize', applyMobileStyles);
    return () => window.removeEventListener('resize', applyMobileStyles);
  }, []);

  useEffect(() => {
    const observerTarget = sectionRef.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !hasAnimated) {
          animateCounters();
          setHasAnimated(true);
        }
      },
      { threshold: 0.25 }
    );
    if (observerTarget) observer.observe(observerTarget);
    return () => { if (observerTarget) observer.unobserve(observerTarget); };
  }, [hasAnimated]);

  const animateCounters = () => {
    const durations = 2300;
    const steps = 80;
    const intervalTime = durations / steps;
    let step = 0;
    const interval = setInterval(() => {
      setCounts(prev =>
        prev.map((count, i) => {
          const nextVal = count + stats[i].target / steps;
          return nextVal >= stats[i].target ? stats[i].target : nextVal;
        })
      );
      step++;
      if (step >= steps) clearInterval(interval);
    }, intervalTime);
  };

  return (
    <>
      <section id="about" className="about-section" ref={sectionRef} data-aos="fade-up">
        <div className="about-container">
          <div className="about-text">

            {/* Image left + text right, full bleed layout */}
            <div className="about-body">
              <div className="about-image-col">
                <img src="/images/about-footer-img.jpg" alt="Adept Knowledge Technologies facility" className="about-side-img-primary" />
              </div>

              <div className="about-text-col">
                <p className="about-subtitle">Where Smart Tech Meets <span className="about-subtitle-highlight">Livestock</span> Care</p>
                <p>
                  Founded in 2020, Adept Knowledge Technologies delivers innovative technology solutions that solve
                  real-world challenges with measurable impact. Our mission is to create technology that
                  fuels progress, efficiency, and sustainable growth.
                </p>
                <p>
                  With a passionate team spanning autonomous sensing systems, artificial intelligence and
                  business development, we bring multidisciplinary expertise to the table. One of our key
                  focus areas is animal health and dairy sustainability, where we leverage smart tech to
                  tackle issues like <strong>Mastitis - a silent yet economically damaging disease affecting
                  India's dairy industry.</strong>
                </p>
                <p>
                  We are a <strong>Startup India-recognized</strong> company, registered under DPIIT with recognition
                  number DPII158932, reaffirming our commitment to innovation, scalability, and national
                  development. At Adept Knowledge Technologies, we believe that technology should empower communities, enhance
                  livestock welfare, and drive long-term economic growth. Whether it's transforming cities
                  or improving the lives of farmers and animals, our team is committed to delivering
                  solutions that matter.
                </p>
              </div>
            </div>

            {/* MOU Partner Ribbon */}
            <div className="mou-ribbon">
              <span className="mou-ribbon-label">Signed MOU - Trusted Partner</span>
              <div className="mou-ribbon-logos">
                <div className="mou-ribbon-card">
                  <img src="/images/PSGCAS.svg" alt="PSG College of Arts & Science" />
                </div>
                {/* <div className="mou-ribbon-divider" /> */}
                {/* <div className="mou-ribbon-card">
                  <img src="/images/Vision.png" alt="Vision Intelligence Limited" className="mou-vision-logo" />
                </div> */}
              </div>
            </div>

            <div className="impact-row">
              {stats.map((stat, i) => (
                <div className="impact-item" key={i}>
                  <h3>
                    {stat.prefix || ''}
                    <span className="counter-number">{Math.min(Math.round(counts[i]), stats[i].target)}</span>
                    <span className="counter-suffix">{stat.suffix}</span>
                  </h3>
                  <p>{stat.description}</p>
                  {i < stats.length - 1 && <div className="vertical-line" />}
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Full-width blended image — sits just above the footer */}
      <div className="about-footer-image-wrapper">
        <img src="/images/about-footer-img2.jpg" alt="" className="about-footer-image" />
      </div>
    </>
  );
};

export default About;
